import { ApiKey } from "@/types/api-keys";
import { Product } from "@/types/subscription";

export async function createApiKey(
  organizationId: string,
  userId: string,
  product: Product,
): Promise<ApiKey> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 30000);

  try {
    const response = await fetch("/api/api-keys", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        organizationId,
        userId,
        product,
      }),
      cache: "no-store",
      signal: controller.signal,
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to create API key");
    }

    try {
      const { apiKey } = await response.json();
      return apiKey;
    } catch (error: any) {
      throw new Error(error.message || "Failed to create API key");
    }
  } finally {
    clearTimeout(timeoutId);
  }
}

export async function deleteApiKey(
  apiKeyId: string,
  organizationId: string,
): Promise<void> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 30000);

  try {
    const response = await fetch("/api/api-keys", {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
      },
      cache: "no-store",
      body: JSON.stringify({ apiKeyId, organizationId }),
      signal: controller.signal,
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || "Failed to delete API key");
    }
  } finally {
    clearTimeout(timeoutId);
  }
}

export async function getApiKeys(
  organizationId: string,
  forceRefresh = false,
): Promise<ApiKey[]> {
  const url = forceRefresh
    ? `/api/api-keys?orgId=${organizationId}&_=${Date.now()}`
    : `/api/api-keys?orgId=${organizationId}`;

  const response = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
    cache: forceRefresh ? "no-store" : "force-cache",
    next: forceRefresh
      ? undefined
      : {
          tags: ["api-keys"],
          revalidate: 3600,
        },
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.error || "Failed to get API keys");
  }

  try {
    const { apiKeys } = await response.json();
    return apiKeys;
  } catch (error: any) {
    throw new Error(error.message || "Failed to get API keys");
  }
}
